import React from "react";
import { Tooltip } from "primereact/tooltip";
import { T } from "services/lang/LanguageService";
import { fmtDatetime } from "utils/time-utils";
import { useDisplayTimezone } from "hooks/useTimezone";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { DataDisplayType, ValueChangeIndicatorProps } from "./ValueChangeIndicatorTypes";
import { formatNumber, getTextColor } from "../charts/utils";
import ValueContainer from "./ValueContainer";

const LastValueTemplateForAverage = (
  props: ValueChangeIndicatorProps & {
    dataRange?: DatasetDataInfoResponse[];
    setDialog: (isVisible: boolean) => void;
  }
) => {
  const displayTz = useDisplayTimezone();

  const getItemsForAverageValues = (currentDataRange: DatasetDataInfoResponse[]) =>
    currentDataRange.map(ds => {
      const values = (ds.data ?? []).map(p => p?.value).filter((v): v is number => Number.isFinite(v));
      const count = values.length;
      const avg = count ? values.reduce((a, b) => a + b, 0) / count : null;
      const dataArray = ds.data ?? [];
      return { ds, avg, count, dataArray };
    });

  if (
    Array.isArray(props.dataRange) &&
    props.dataRange.length > 0 &&
    props.dataDisplayType === DataDisplayType.AVERAGE_VALUE
  ) {
    const allUnits = new Set(props.dataRange.map(ds => ds.unit).filter(Boolean));
    const sameUnit = allUnits.size === 1 ? [...allUnits][0] : undefined;

    return (
      <div
        className={`avg-values-container flex flex-column gap-2 ${props.highlightValue ? "highlight-value" : ""}`}
        style={{ color: props.highlightValue ? "" : getTextColor(props.theme) }}
        onDoubleClick={() => props.setDialog(true)}
      >
        {getItemsForAverageValues(props.dataRange).map(({ ds, avg, count, dataArray }) => {
          const dp = props.decimalPlaces ?? ds.decimalPlaces;
          const unitToShow = sameUnit ?? ds.unit;
          const tooltipId = `avg-value-tooltip`;
          const first = dataArray?.[0];
          const last = dataArray?.[dataArray.length - 1];
          return (
            <ValueContainer
              key={ds.identifier}
              id={tooltipId}
              value={avg !== null ? formatNumber(avg, dp, props.convertToAbsoluteValues) : "–"}
              unit={!props.hideUnit ? unitToShow : undefined}
              color={getTextColor(props.theme)}
            >
              <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
                {count > 0 ? (
                  <div className="flex flex-column gap-1">
                    <div>
                      <b>{T("word.from")}:</b> {fmtDatetime(first?.key, displayTz)}
                    </div>
                    <div>
                      <b>{T("word.to")}:</b> {fmtDatetime(last?.key, displayTz)}
                    </div>
                    <div>
                      <b>{T("action.count")}:</b> {count}
                    </div>
                  </div>
                ) : (
                  T("word.no-data")
                )}
              </Tooltip>
            </ValueContainer>
          );
        })}
      </div>
    );
  }
  return null;
};

export default LastValueTemplateForAverage;
